import { useMemo } from 'react';
import { mkConfig, generateCsv, download, ConfigOptions } from 'export-to-csv';
import { isPlainObject } from 'lodash';
import { PlainObject } from '@/models/plainObject';

const defaultOptions: ConfigOptions = { useKeysAsHeaders: true };

export const useCsvConfig = (initialConfig: ConfigOptions = defaultOptions) => {
    const config = useMemo(() => {
        return mkConfig(initialConfig);
    }, [initialConfig]);

    const onGenerateCSV = (data: PlainObject[]) => {
        const dataPrepared = data.map((item) => prepareObject(item));
        return generateCsv(config)(dataPrepared);
    };

    const onGenerateCSVWithDownload = (data: PlainObject[]) => {
        const csvContent = onGenerateCSV(data);

        download(config)(csvContent);
    };

    return {
        config,
        onGenerateCSV,
        onGenerateCSVWithDownload,
    };
};

const prepareObject = (obj: PlainObject, prefix = ''): PlainObject => {
    if (!isPlainObject(obj)) {
        return obj;
    }

    return Object.keys(obj).reduce((acc: PlainObject, key: string) => {
        const value = obj[key];
        const newKey = prefix ? `${prefix}.${key}` : key;

        if (
            value === null ||
            typeof value === 'string' ||
            typeof value === 'number' ||
            typeof value === 'boolean'
        ) {
            acc[newKey] = valueTransformPipeline(value);
        } else if (isPlainObject(value)) {
            Object.assign(acc, prepareObject(value as PlainObject, newKey));
        }
        // Skip arrays and other non-primitive types

        return acc;
    }, {});
};

const valueTransformPipeline = (value: unknown) => {
    const transformations = [getEmptyValue, getBooleanValue];

    return transformations.reduce((acc, transform) => {
        return transform(acc);
    }, value);
};

const getEmptyValue = (value: unknown) => (!value ? '-' : value);

const getBooleanValue = (value: unknown) =>
    typeof value === 'boolean' ? (value ? 'Ja' : 'Nein') : value;
